/**
 * Formata valor em centavos para moeda BRL (ex: 2990 → "R$ 29,90").
 */
export function formatPrice(centavos: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(centavos / 100);
}

/**
 * Formata duração em milissegundos para texto compacto: "5s", "3min", "2h", "1d".
 * Usado em contadores de "atualizado há X" e badges de tempo.
 */
export function formatCompactDuration(ms: number): string {
  const sec = Math.max(0, Math.floor(ms / 1000));
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}min`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  return `${Math.floor(hr / 24)}d`;
}

/**
 * Formata tamanho de arquivo em bytes para unidade legível (B, KB, MB, GB).
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

/**
 * Lê uma data ISO para `Date` sem deixar o fuso mudar o dia.
 *
 * Data sem hora (`2026-09-10`, campo `date` do backend) é ancorada ao meio-dia
 * LOCAL: `new Date('2026-09-10')` é meia-noite em UTC, que no Brasil ainda é o
 * dia 9 — a data aparecia um dia antes do que o banco guarda.
 *
 * Datetime (`2026-09-10T14:30:00Z`) passa intacto: o fuso está escrito, e fixar
 * meio-dia estragaria o horário.
 */
export function parseLocalDate(iso: string): Date {
  return /^\d{4}-\d{2}-\d{2}$/.test(iso) ? new Date(`${iso}T12:00:00`) : new Date(iso);
}

/**
 * Formata data ISO para formato brasileiro (dd/mm/aaaa).
 * Retorna '—' se a data for nula/indefinida.
 */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return parseLocalDate(iso).toLocaleDateString('pt-BR');
}

/**
 * Converte um ISO datetime para a data local no formato `YYYY-MM-DD`.
 *
 * Usar `iso.slice(0, 10)` corromperia a data quando o horário em UTC cruza a
 * meia-noite local (ex.: prazo às 23:59 BRT vira o dia seguinte em UTC). Aqui a
 * data é derivada do horário local, casando com o que o usuário vê.
 */
export function toLocalISODate(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * Formata data extraída (potencialmente em range) para exibição.
 *
 * Casos:
 * - Data simples sem hora:                "05/05/2026"
 * - Data simples com hora:                "05/05/2026 às 14:00"
 * - Range mesma data, sem hora:           "05/05/2026"
 * - Range mesma data, hora ínicio = fim:  "05/05/2026 às 14:00"
 * - Range mesma data, horas distintas:    "05/05/2026 das 09:00 às 17:00"
 * - Range entre dias, hora compartilhada: "05/05/2026 a 12/05/2026 às 14:00"
 * - Range entre dias, horas distintas:    "05/05/2026 às 09:00 a 12/05/2026 às 17:00"
 */
export function formatExtractedDateRange(d: {
  data: string;
  hora: string | null;
  data_fim: string | null;
  hora_fim: string | null;
}): string {
  const startDate = new Date(d.data + 'T12:00:00').toLocaleDateString('pt-BR');
  const hasRange = d.data_fim && d.data_fim !== d.data;
  const sameHour = d.hora_fim && d.hora === d.hora_fim;

  if (!hasRange) {
    if (d.data_fim === d.data && d.hora && d.hora_fim && !sameHour) {
      return `${startDate} das ${d.hora} às ${d.hora_fim}`;
    }
    return d.hora ? `${startDate} às ${d.hora}` : startDate;
  }

  const endDate = new Date(d.data_fim! + 'T12:00:00').toLocaleDateString('pt-BR');
  if (sameHour && d.hora) {
    return `${startDate} a ${endDate} às ${d.hora}`;
  }
  if (d.hora && d.hora_fim) {
    return `${startDate} às ${d.hora} a ${endDate} às ${d.hora_fim}`;
  }
  return `${startDate} a ${endDate}`;
}

/**
 * Formata data ISO para formato brasileiro com dia da semana.
 * Ex: "24/03/2026 (terça-feira)"
 */
export function formatDateWithWeekday(dateStr: string): string {
  const d = parseLocalDate(dateStr);
  const weekday = d.toLocaleDateString('pt-BR', { weekday: 'long' });
  const date = d.toLocaleDateString('pt-BR');
  return `${date} (${weekday})`;
}

/**
 * Aplica máscara de CPF enquanto o usuário digita: XXX.XXX.XXX-XX
 */
export function maskCpf(value: string): string {
  const d = value.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

/**
 * Máscara parcial de CPF para exibição em telas: mantém visíveis o segundo
 * e terceiro blocos (úteis pro próprio usuário se reconhecer) e oculta os
 * extremos (3 primeiros dígitos + 2 últimos). Formato: `***.560.050-**`.
 *
 * Use em tabelas, cards e listas onde o CPF aparece para o próprio titular
 * mas a tela pode ser vista em apresentação/local público. Aceita entrada
 * com ou sem máscara. Retorna '-' para entradas inválidas.
 */
export function maskCpfPrivate(value: string | null | undefined): string {
  if (!value) return '-';
  const d = value.replace(/\D/g, '');
  if (d.length !== 11) return '-';
  return `***.${d.slice(3, 6)}.${d.slice(6, 9)}-**`;
}

/**
 * Aplica máscara de CNPJ enquanto o usuário digita: XX.XXX.XXX/XXXX-XX
 */
export function maskCnpj(value: string): string {
  const d = value.replace(/\D/g, '').slice(0, 14);
  if (d.length <= 2) return d;
  if (d.length <= 5) return `${d.slice(0, 2)}.${d.slice(2)}`;
  if (d.length <= 8) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`;
  if (d.length <= 12) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}

/**
 * Aplica máscara de telefone: (XX) XXXXX-XXXX ou (XX) XXXX-XXXX
 */
export function maskTelefone(value: string): string {
  const d = value.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 2) return d.length ? `(${d}` : '';
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

/**
 * Aplica máscara de CEP: XXXXX-XXX
 */
export function maskCep(value: string): string {
  const d = value.replace(/\D/g, '').slice(0, 8);
  if (d.length <= 5) return d;
  return `${d.slice(0, 5)}-${d.slice(5)}`;
}

// --- Validação de documentos ---

/**
 * Verifica se o CPF é válido (dígitos verificadores, algoritmo mod 11).
 * Aceita com ou sem máscara.
 */
export function validateCpf(rawCpf: string): boolean {
  const digits = rawCpf.replace(/\D/g, '');
  if (digits.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(digits)) return false;
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += parseInt(digits[i]) * (10 - i);
  let remainder = (sum * 10) % 11;
  if (remainder === 10) remainder = 0;
  if (remainder !== parseInt(digits[9])) return false;
  sum = 0;
  for (let i = 0; i < 10; i++) sum += parseInt(digits[i]) * (11 - i);
  remainder = (sum * 10) % 11;
  if (remainder === 10) remainder = 0;
  if (remainder !== parseInt(digits[10])) return false;
  return true;
}

/**
 * Verifica se o CNPJ é válido (dígitos verificadores).
 * Aceita com ou sem máscara.
 */
export function validateCnpj(rawCnpj: string): boolean {
  const digits = rawCnpj.replace(/\D/g, '');
  if (digits.length !== 14) return false;
  if (/^(\d)\1{13}$/.test(digits)) return false;
  const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  let sum = 0;
  for (let i = 0; i < 12; i++) sum += parseInt(digits[i]) * weights1[i];
  let remainder = sum % 11;
  if (remainder < 2) { if (parseInt(digits[12]) !== 0) return false; }
  else { if (parseInt(digits[12]) !== 11 - remainder) return false; }
  sum = 0;
  for (let i = 0; i < 13; i++) sum += parseInt(digits[i]) * weights2[i];
  remainder = sum % 11;
  if (remainder < 2) { if (parseInt(digits[13]) !== 0) return false; }
  else { if (parseInt(digits[13]) !== 11 - remainder) return false; }
  return true;
}

const LOWERCASE_WORDS = new Set([
  'de', 'do', 'da', 'dos', 'das', 'a', 'ao', 'aos', 'à', 'às',
  'contra', 'para', 'por', 'com', 'em', 'e', 'ou', 'o', 'os', 'as',
  'no', 'na', 'nos', 'nas', 'pelo', 'pela', 'pelos', 'pelas', 'sobre', 'sob',
]);

/**
 * Formata número de processo no padrão CNJ: NNNNNNN-DD.AAAA.J.TR.OOOO
 */
export function formatNumeroProcesso(numero: string): string {
  if (/^\d{7}-\d{2}\.\d{4}\.\d\.\d{2}\.\d{4}$/.test(numero)) {
    return numero;
  }

  const digits = numero.replace(/\D/g, '');
  if (digits.length === 20) {
    return `${digits.slice(0, 7)}-${digits.slice(7, 9)}.${digits.slice(9, 13)}.${digits.slice(13, 14)}.${digits.slice(14, 16)}.${digits.slice(16, 20)}`;
  }

  return numero;
}

/**
 * Máscara progressiva para input do número CNJ: aceita dígitos crus (digitados
 * ou colados) e insere separadores conforme o usuário avança, no padrão
 * NNNNNNN-DD.AAAA.J.TR.OOOO. Limita a 20 dígitos.
 */
export function maskNumeroProcesso(value: string): string {
  const d = value.replace(/\D/g, '').slice(0, 20);
  let out = d.slice(0, 7);
  if (d.length > 7) out += '-' + d.slice(7, 9);
  if (d.length > 9) out += '.' + d.slice(9, 13);
  if (d.length > 13) out += '.' + d.slice(13, 14);
  if (d.length > 14) out += '.' + d.slice(14, 16);
  if (d.length > 16) out += '.' + d.slice(16, 20);
  return out;
}

function toTitleCase(texto: string): string {
  return texto
    .toLowerCase()
    .split(/\s+/)
    .map((word, index) => {
      if (index > 0 && LOWERCASE_WORDS.has(word)) {
        return word;
      }
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
}

/**
 * Converte classe processual para Title Case, mantendo preposições/artigos em minúsculo.
 * Ex: "CUMPRIMENTO DE SENTENçA" → "Cumprimento de Sentença"
 */
export function toTitleCaseClasse(classe: string): string {
  return toTitleCase(classe);
}

/**
 * Partículas que ficam em minúscula no meio de um nome. As preposições da classe
 * processual (LOWERCASE_WORDS) mais as de sobrenome estrangeiro — "van der",
 * "von", "del", "di" —, que aparecem em nome brasileiro com a mesma frequência
 * que "da" e "dos". Espelha `_PARTICULAS` do backend (services/secretario/extracao.py).
 */
const PARTICULAS_DE_NOME = new Set([
  ...LOWERCASE_WORDS,
  'van', 'von', 'der', 'den', 'del', 'della', 'di', 'du', 'la', 'le', 'y',
]);

/** Algarismo romano puro ("II", "XVIII") — sufixo de nome que não vira "Ii". */
const RE_ROMANO = /^[IVXLCDM]+$/;

/** Nome já em caixa mista ("McKenzie", "d'Ávila") — quem digitou sabia o que fazia. */
const RE_CAIXA_MISTA = /[a-zà-ÿ][A-ZÀ-Ý]/;

/** Sobe a primeira letra e as que seguem apóstrofo ou hífen: "sant'anna" → "Sant'Anna". */
function capitalizarParticula(palavra: string): string {
  return palavra.replace(/(^|['’-])([a-zà-ÿ])/g, (_, separador: string, letra: string) =>
    separador + letra.toUpperCase(),
  );
}

/**
 * Nome de pessoa em caixa alta — como vem do OCR de documento e de cadastro
 * importado — reduzido a Title Case: "ADRIANO DA SILVA ARAUJO" → "Adriano da
 * Silva Araujo".
 *
 * Nome de gente tem mais forma que classe processual, e a versão que só subia o
 * caractere 0 de cada palavra desfigurava nomes brasileiros comuns: "SANT'ANNA"
 * virava "Sant'anna", "ANA-LÚCIA" virava "Ana-lúcia", "JOÃO PAULO II" virava
 * "João Paulo Ii". E arrasava o que já estava certo — "McKenzie" virava
 * "Mckenzie" —, por isso texto que JÁ tem caixa mista passa intacto.
 */
export function toTitleCaseNome(nome: string): string {
  return nome
    .split(/\s+/)
    .map((palavra, index) => {
      if (!palavra) return palavra;
      if (RE_ROMANO.test(palavra)) return palavra; // II, III, XVIII
      if (RE_CAIXA_MISTA.test(palavra)) return palavra; // McKenzie, d'Ávila
      const minuscula = palavra.toLowerCase();
      if (index > 0 && PARTICULAS_DE_NOME.has(minuscula)) return minuscula;
      return capitalizarParticula(minuscula);
    })
    .join(' ');
}

/**
 * Mascara um email para exibição parcial, preservando 2 caracteres do início e
 * 2 do fim do local-part. Ex: "adriano@jurisapp.ia.br" -> "ad***no@jurisapp.ia.br".
 *
 * Para local-parts com 4 caracteres ou menos, retorna o email original (mascarar
 * resultaria em algo ilegível ou que não esconde nada útil).
 */
export function maskEmail(email: string | null | undefined): string {
  if (!email) return '';
  const at = email.lastIndexOf('@');
  if (at <= 0) return email;
  const local = email.slice(0, at);
  const domain = email.slice(at);
  if (local.length <= 4) return email;
  const prefix = local.slice(0, 2);
  const suffix = local.slice(-2);
  const stars = '*'.repeat(Math.max(3, local.length - 4));
  return `${prefix}${stars}${suffix}${domain}`;
}

/**
 * Primeiro e último nome ("Adriano da Silva Araujo" → "Adriano Araujo") — onde o
 * nome inteiro não cabe, como o rodapé do menu.
 */
export function primeiroEUltimoNome(nome: string | null | undefined): string {
  const partes = (nome ?? '').trim().split(/\s+/).filter(Boolean);
  if (partes.length <= 2) return partes.join(' ');
  return `${partes[0]} ${partes[partes.length - 1]}`;
}
